import { ExternalLink, CheckCircle2, Mail, Award } from 'lucide-react';
import { BRAND } from '@/data/mockData';
import { FEEDBACK } from '@/constants/testIds';
import ParticlesBg from '@/components/common/ParticlesBg';

const platforms = [
  { name: 'UrbanPro', url: BRAND.reviews.urbanpro, tid: FEEDBACK.urbanproLink, color: 'from-orange-500/30 to-red-500/20' },
  { name: 'Trustpilot', url: BRAND.reviews.trustpilot, tid: FEEDBACK.trustpilotLink, color: 'from-green-500/30 to-emerald-500/20' },
  { name: 'Google', url: BRAND.reviews.google, tid: FEEDBACK.googleLink, color: 'from-blue-500/30 to-cyan-500/20' },
];

const Feedback = () => {
  return (
    <>
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow" />
        <ParticlesBg count={25} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">Feedback & Certification</div>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-4xl font-semibold">
            Complete your feedback. <span className="text-brand-cyan">Claim your certificate.</span>
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl">
            A short verified feedback on any of the three platforms below unlocks your BYMAT Automation course completion certificate.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {platforms.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              data-testid={p.tid}
              className="group relative glass rounded-2xl p-7 overflow-hidden hover:border-brand-cyan/40 transition"
            >
              <div className={`absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br ${p.color} blur-3xl opacity-60 group-hover:opacity-100 transition`} />
              <div className="relative">
                <div className="text-xs tracking-[0.3em] uppercase font-mono text-brand-cyan/80">Leave Feedback</div>
                <h3 className="mt-3 font-display text-2xl font-bold">{p.name}</h3>
                <p className="mt-3 text-sm text-white/70">Share your honest experience. Takes ~2 minutes.</p>
                <div className="mt-6 inline-flex items-center gap-2 text-brand-cyan font-semibold text-sm">
                  Open {p.name} <ExternalLink className="w-4 h-4" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-strong rounded-3xl p-8 sm:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-mono text-brand-cyan/90">
                  <Award className="w-3.5 h-3.5" /> Certification Workflow
                </div>
                <h2 className="font-display mt-3 text-2xl sm:text-3xl font-bold tracking-tight">4-step certificate claim</h2>
                <ol className="mt-6 space-y-4">
                  {[
                    'Submit feedback on any platform (UrbanPro / Trustpilot / Google).',
                    `Email a screenshot proof to ${BRAND.email}.`,
                    'Admin verifies the feedback within 1 business day.',
                    'BYMAT issues a signed digital course completion certificate.',
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-brand-cyan text-black text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span className="text-white/80 text-sm leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
                <a
                  href={`mailto:${BRAND.email}?subject=Course%20Completion%20Certificate%20Request&body=Hi%20BYMAT%20Team%2C%0A%0APlease%20find%20attached%20my%20feedback%20screenshot%20proof.%20Kindly%20issue%20my%20course%20completion%20certificate.%0A%0AThanks%2C`}
                  data-testid={FEEDBACK.claimCertButton}
                  className="mt-7 inline-flex items-center gap-2 bg-brand-cyan text-black font-semibold px-6 py-3 rounded-lg btn-glow hover:bg-brand-cyanSoft transition"
                >
                  <Mail className="w-4 h-4" /> Email Certificate Proof
                </a>
              </div>

              <div className="relative">
                <div className="aspect-[4/5] rounded-2xl glass overflow-hidden p-6 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase font-mono text-brand-cyan">Certificate of Completion</div>
                    <div className="font-display text-2xl font-black mt-3 font-semibold">BYMAT Automation</div>
                    <div className="text-sm text-white/60 mt-1">presented to</div>
                    <div className="font-display text-xl font-bold mt-1 text-brand-cyan">[ Your Name ]</div>
                  </div>
                  <div className="mt-6">
                    <CheckCircle2 className="w-10 h-10 text-brand-cyan mb-3" />
                    <p className="text-sm text-white/70">For successfully completing the GenAI Automation Testing program — Selenium / Playwright / Appium with AI integrations.</p>
                  </div>
                  <div className="flex items-end justify-between pt-6 border-t border-white/5">
                    <div>
                      <div className="text-xs text-white/50">Founder & Mentor</div>
                      <div className="font-display font-bold">Rahul Ganjare</div>
                    </div>
                    <div className="text-xs text-white/40 font-mono">cert-bymat-2026</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Feedback;
